import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlatformIcon, PLATFORM_META } from "@/components/PlatformBadge";
import { Check, ArrowRight, Loader2, Sparkles, Globe, Rocket } from "lucide-react";
import { toast } from "sonner";
import NueraLogo from "@/components/NueraLogo";

const INDUSTRIES = ["Higher Education", "E-commerce", "SaaS / Technology", "Healthcare", "Finance", "Hospitality", "Real Estate", "Non-profit", "Other"];
const PLATFORMS = ["facebook", "instagram", "linkedin", "ga4"];

function Stepper({ step }) {
  const labels = ["Your brand", "Connect", "Build"];
  return (
    <div className="mb-10 flex items-center justify-center gap-2">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center gap-2">
          <div className={`grid h-8 w-8 place-items-center rounded-full text-sm font-bold transition-colors ${i <= step ? "bg-brand-primary text-brand-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {i < step ? <Check className="h-4 w-4" /> : i + 1}
          </div>
          <span className={`text-sm font-medium ${i <= step ? "text-foreground" : "text-muted-foreground"}`}>{l}</span>
          {i < labels.length - 1 && <div className="mx-2 h-px w-8 bg-border" />}
        </div>
      ))}
    </div>
  );
}

export default function Onboarding() {
  const { org, refreshOrg, setOrg } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [building, setBuilding] = useState(false);

  const [brand, setBrand] = useState({
    name: org?.name || "",
    website: org?.website || "",
    industry: org?.industry || "",
    logo_url: org?.logo_url || "",
    primary_color: org?.primary_color || "#2563EB",
    secondary_color: org?.secondary_color || "#F97316",
  });

  const { data: integrations, refetch } = useQuery({
    queryKey: ["integrations"],
    queryFn: async () => (await api.get("/integrations")).data,
    enabled: step === 1,
  });

  const set = (k) => (e) => setBrand({ ...brand, [k]: e.target.value });

  const scrape = async () => {
    if (!brand.website) { toast.error("Enter a website URL first"); return; }
    setScraping(true);
    try {
      const { data } = await api.post("/org/scrape-website", { url: brand.website });
      setBrand((b) => ({
        ...b,
        logo_url: data.logo_url || b.logo_url,
        primary_color: data.primary_color || b.primary_color,
        name: b.name || data.name || b.name,
      }));
      setOrg({ ...org, ...brand, logo_url: data.logo_url || brand.logo_url });
      toast.success("Pulled what we could from your website");
    } catch {
      toast.error("Couldn't read that site — you can add details manually");
    } finally {
      setScraping(false);
    }
  };

  const saveBrand = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/org/brand", brand);
      setOrg(data);
      setStep(1);
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const connect = async (platform) => {
    try {
      await api.post("/integrations/connect", { platform });
      toast.success(`${PLATFORM_META[platform].label} connected`);
      refetch();
    } catch {
      toast.error("Connection failed");
    }
  };

  const build = async () => {
    setBuilding(true);
    try {
      await api.post("/org/build");
      await refreshOrg();
      qc.invalidateQueries();
      toast.success("Your dashboard is ready!");
      navigate("/");
    } catch {
      toast.error("Something went wrong building your dashboard");
      setBuilding(false);
    }
  };

  const connectedCount = integrations?.filter((i) => i.connected).length || 0;

  return (
    <div className="min-h-screen bg-background px-4 py-10 sm:py-16">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex justify-center"><NueraLogo className="h-8 w-auto" /></div>
        <Stepper step={step} />

        {step === 0 && (
          <div className="animate-fade-up rounded-3xl border border-border/60 bg-card p-6 shadow-sm sm:p-8">
            <h1 className="font-heading text-2xl font-bold tracking-tight">Tell us about your brand</h1>
            <p className="mt-1 text-sm text-muted-foreground">We'll weave your identity into every screen.</p>
            <div className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label>Company / brand name</Label>
                <Input data-testid="onb-name" value={brand.name} onChange={set("name")} placeholder="AM Jain College" />
              </div>
              <div className="space-y-1.5">
                <Label>Website URL</Label>
                <div className="flex gap-2">
                  <Input data-testid="onb-website" value={brand.website} onChange={set("website")} placeholder="https://yourbrand.com" />
                  <Button type="button" variant="outline" onClick={scrape} disabled={scraping} data-testid="onb-scrape" className="shrink-0 rounded-xl">
                    {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Globe className="mr-1.5 h-4 w-4" />Fetch</>}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">We'll try to pull your logo and colours automatically.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Industry</Label>
                <Select value={brand.industry} onValueChange={(v) => setBrand({ ...brand, industry: v })}>
                  <SelectTrigger data-testid="onb-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Logo URL</Label>
                <Input data-testid="onb-logo" value={brand.logo_url} onChange={set("logo_url")} placeholder="https://.../logo.png" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Primary colour</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" data-testid="onb-primary" value={brand.primary_color} onChange={set("primary_color")} className="h-10 w-12 cursor-pointer rounded-lg border border-border" />
                    <Input value={brand.primary_color} onChange={set("primary_color")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Secondary colour</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" data-testid="onb-secondary" value={brand.secondary_color} onChange={set("secondary_color")} className="h-10 w-12 cursor-pointer rounded-lg border border-border" />
                    <Input value={brand.secondary_color} onChange={set("secondary_color")} />
                  </div>
                </div>
              </div>
            </div>
            <Button data-testid="onb-continue" onClick={saveBrand} disabled={saving || !brand.name} className="mt-6 w-full rounded-full bg-brand-primary text-brand-primary-foreground hover:opacity-90">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Continue <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-up">
            <div className="mb-6 text-center">
              <h1 className="font-heading text-2xl font-bold tracking-tight">Connect your marketing</h1>
              <p className="mt-1 text-sm text-muted-foreground">Link your channels so Nuera can bring in your data.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {(integrations || PLATFORMS.map((p) => ({ platform: p, label: PLATFORM_META[p].label, connected: false }))).map((it) => {
                const meta = PLATFORM_META[it.platform];
                return (
                  <div key={it.platform} data-testid={`onb-card-${it.platform}`} className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ backgroundColor: `${meta.color}1a` }}>
                        <PlatformIcon platform={it.platform} className="h-6 w-6" />
                      </span>
                      <div>
                        <p className="font-semibold">{meta.label}</p>
                        <p className="text-xs text-muted-foreground">Connect {it.platform === "ga4" ? "GA4 Property" : it.platform === "facebook" ? "Facebook Page" : it.platform === "instagram" ? "Instagram Business" : "Company Page"}</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      {it.connected ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700"><Check className="h-4 w-4" />Connected</span>
                      ) : (
                        <Button data-testid={`onb-connect-${it.platform}`} onClick={() => connect(it.platform)} variant="outline" className="w-full rounded-full">Connect <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 flex gap-3">
              <Button variant="ghost" onClick={() => setStep(0)} className="rounded-full">Back</Button>
              <Button data-testid="onb-to-build" onClick={() => setStep(2)} disabled={connectedCount === 0} className="flex-1 rounded-full bg-brand-primary text-brand-primary-foreground hover:opacity-90">
                Continue ({connectedCount} connected) <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up rounded-3xl border border-border/60 bg-card p-8 text-center shadow-sm">
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-brand-primary/10">
              <Rocket className="h-8 w-8 text-brand-primary" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold tracking-tight">Build my dashboard</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Nuera will pull in your history and assemble your command centre automatically — no manual widgets.</p>
            <Button data-testid="onb-build" onClick={build} disabled={building} className="mt-6 w-full rounded-full bg-brand-primary text-brand-primary-foreground hover:opacity-90">
              {building ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Building your dashboard…</> : <><Sparkles className="mr-2 h-4 w-4" />Build My Dashboard</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
